import { useContext } from "react"
import {
  LayoutListIcon,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  X,
  BarChart,
  PieChart,
  CircleDot,
} from "lucide-react"
import styles from "./layout.module.css"
import { TemplateContext } from "../../../../../../../../contexts/TemplateContext"
import { AlignmentType, ChartType, Template } from "../../../../../../../../sdkStructs"

const alignmentOptions: {
  value: AlignmentType
  icon: JSX.Element
  label: string
}[] = [
  { value: "Left", icon: <AlignStartVertical size={16} />, label: "Left" },
  { value: "Center", icon: <AlignCenterVertical size={16} />, label: "Center" },
  { value: "Right", icon: <AlignEndVertical size={16} />, label: "Right" },
  { value: "None", icon: <X size={16} />, label: "None" },
]

const chartOptions: { value: ChartType; icon: JSX.Element; label: string }[] = [
  { value: "Bar", icon: <BarChart size={16} />, label: "Bar Chart" },
  { value: "Pie", icon: <PieChart size={16} />, label: "Pie Chart" },
  { value: "Donut", icon: <CircleDot size={16} />, label: "Donut Chart" },
]

interface ChooserProps {
  value: AlignmentType | ChartType
  onChange: (value: AlignmentType | ChartType) => void
  label: string
  options: {
    value: AlignmentType | ChartType
    icon: JSX.Element
    label: string
  }[]
}

function Chooser({ value, onChange, label, options }: Readonly<ChooserProps>) {
  return (
    <>
      <label htmlFor={`${label.toLowerCase()}-input`}>{label}</label>
      <div className={styles.segmentedControl}>
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={`${styles.segmentButton} ${value === option.value ? styles.selected : ""}`}
            title={option.label}
          >
            {option.icon}
          </button>
        ))}
      </div>
    </>
  )
}

export default function Layout() {
  const { template, setTemplate } = useContext(TemplateContext)

  const updateTemplate = <K extends keyof Template>(field: K, value: Template[K]) => {
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return prevTemplate;
      return {
        ...prevTemplate,
        [field]: value,
      };
    });
  }

  const handleLogoAlignmentChange = (newAlignment: AlignmentType) => {
    if (!template) return;
    
    if (newAlignment !== "None" && newAlignment === template.MottoPosition) {
      updateTemplate("MottoPosition", template.LogoPosition);
    }
    updateTemplate("LogoPosition", newAlignment);
  }

  const handleMottoAlignmentChange = (newAlignment: AlignmentType) => {
    if (!template) return;
    
    if (newAlignment !== "None" && newAlignment === template.LogoPosition) {
      updateTemplate("LogoPosition", template.MottoPosition);
    }
    updateTemplate("MottoPosition", newAlignment);
  }

  // If template is null, show loading state
  if (!template) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <LayoutListIcon className={styles.icon} />
          <h1>Layout</h1>
        </div>
        <div className={styles.content}>
          <p>Loading layout settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <LayoutListIcon className={styles.icon} />
        <h1>Layout</h1>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <div className={styles.inputGroup}>
            <Chooser
              value={template.LogoPosition}
              onChange={(value) => handleLogoAlignmentChange(value as AlignmentType)}
              label="Logo Position"
              options={alignmentOptions}
            />
            <div className={styles.inputGroup}>
              <Chooser
                value={template.MottoPosition}
                onChange={(value) => handleMottoAlignmentChange(value as AlignmentType)}
                label="Motto Position"
                options={alignmentOptions}
              />
              <input
                type="text"
                id="motto-input"
                value={template.Motto}
                onChange={(e) => updateTemplate("Motto", e.target.value)}
                placeholder="Enter your motto here..."
                className={`${styles.input} ${template.MottoPosition === "None" ? styles.disabled : ""}`}
                disabled={template.MottoPosition === "None"}
                maxLength={50}
              />
              <span className={styles.characterCount}>{template.Motto.length}/50 characters</span>
            </div>
            <Chooser
              value={template.PageNumberPosition}
              onChange={(value) => updateTemplate("PageNumberPosition", value as AlignmentType)}
              label="Page Number Position"
              options={alignmentOptions}
            />
            <Chooser
              value={template.ChartType}
              onChange={(value) => updateTemplate("ChartType", value as ChartType)}
              label="Chart Type"
              options={chartOptions}
            />
          </div>
        </div>
      </div>
    </div>
  )
}